import React, { useEffect, useRef, useState } from 'react';
import { useFilters, PRESETS } from '../contexts/FilterContext';

// Global date-range presets + custom range + location multi-select.
// `storesOnly` drops everything about dates (presets, custom range, comparison
// basis) for pages that look FORWARD and so have no use for a past window —
// controls that are visible but do nothing read as a bug. The store picker
// stays: it is the one global filter such a page still honours.
export default function FilterBar({ storesOnly = false }) {
  const {
    preset, setPreset,
    startDate, endDate, setCustom,
    locations, selectedLocations, setSelectedLocations,
    groups, me,
    compareMode, setCompareMode,
  } = useFilters();

  // One flat list of named groups for the picker: custom first, then by
  // city, then by province (skip single-store groups — pointless shortcuts).
  const namedGroups = [
    ...groups.custom,
    ...groups.city,
    ...groups.province,
  ].filter(g => g.locations.length > 1);

  const [open, setOpen] = useState(false);
  const ddRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggleLocation = (id) => {
    setSelectedLocations(sel =>
      sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]
    );
  };

  const locationLabel = selectedLocations.length === 0
    ? (me?.locations ? 'My stores' : 'All locations')
    : selectedLocations.length === 1
      ? (locations.find(l => l.ID === selectedLocations[0])?.NAME || '1 location')
      : `${selectedLocations.length} locations`;

  // Selecting a group replaces the selection with its members.
  const selectGroup = (g) => setSelectedLocations([...g.locations]);
  const groupActive = (g) =>
    g.locations.length === selectedLocations.length
    && g.locations.every(id => selectedLocations.includes(id));

  return (
    <div className="filterbar" data-tour="filters">
      {!storesOnly && (<>
      <div className="chip-row" data-tour="presets">
        {PRESETS.map(p => (
          <button
            key={p.id}
            className={`chip ${preset === p.id ? 'active' : ''}`}
            onClick={() => setPreset(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="date-inputs" data-tour="dates">
        <input
          type="date"
          value={startDate}
          max={endDate}
          onChange={e => e.target.value && setCustom(e.target.value, endDate)}
        />
        <span style={{ color: 'var(--ink-muted)' }}>→</span>
        <input
          type="date"
          value={endDate}
          min={startDate}
          onChange={e => e.target.value && setCustom(startDate, e.target.value)}
        />
      </div>
      <div className="chip-row" data-tour="compare">
        <button
          className={`chip ${compareMode === 'prior' ? 'active' : ''}`}
          title="Compare against the preceding period of equal length"
          onClick={() => setCompareMode('prior')}
        >
          vs Prior
        </button>
        <button
          className={`chip ${compareMode === 'priorYear' ? 'active' : ''}`}
          title="Same period last year, weekday-aligned (364 days)"
          onClick={() => setCompareMode('priorYear')}
        >
          vs Last year
        </button>
      </div>
      </>)}
      <div className="dd" ref={ddRef} data-tour="locations">
        <button className="dd-btn" onClick={() => setOpen(o => !o)}>
          📍 {locationLabel} <span style={{ fontSize: 9 }}>▼</span>
        </button>
        {open && (
          <div className="dd-panel">
            {/* Unchecking everything = no filter = all stores (the button
                label above already reads "All stores" in that state). */}
            <div className="dd-actions">
              <button onClick={() => setSelectedLocations(locations.map(l => l.ID))}>Check all</button>
              <button onClick={() => setSelectedLocations([])}>Uncheck all</button>
            </div>
            {namedGroups.length > 0 && (
              <div style={{ padding: '2px 4px 8px', borderBottom: '1px solid var(--grid)', marginBottom: 6 }}>
                <div className="nav-section-label" style={{ color: 'var(--ink-muted)', padding: '0 4px 4px' }}>Groups</div>
                <div className="chip-row">
                  {namedGroups.map(g => (
                    <button
                      key={g.name}
                      className={`chip ${groupActive(g) ? 'active' : ''}`}
                      title={`${g.locations.length} stores`}
                      onClick={() => selectGroup(g)}
                    >
                      {g.name} ({g.locations.length})
                    </button>
                  ))}
                </div>
              </div>
            )}
            {locations.map(l => (
              <label key={l.ID} className="dd-option">
                <input
                  type="checkbox"
                  checked={selectedLocations.includes(l.ID)}
                  onChange={() => toggleLocation(l.ID)}
                />
                {l.NAME}
                {l.CITY && <span className="city">{l.CITY}</span>}
              </label>
            ))}
            {locations.length === 0 && <div className="empty">No locations loaded</div>}
          </div>
        )}
      </div>
    </div>
  );
}
